import React, { useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import "../css/delivery.scss";

const DELIVERY_ITEMS = [
  {
    id: "shipping-methods",
    title: "Shipping Methods & Destinations",
    content: (
      <>
        <p>
          Available destinations, carriers, delivery estimates, and charges are
          shown at checkout after you enter the delivery address. A shipping
          estimate is not a guaranteed delivery date.
        </p>
        <p>
          <strong>Tracked Shipping:</strong>
        </p>
        <ul>
          <li>Tracking is sent when it is available from the carrier.</li>
          <li>Some remote destinations may require additional transit time.</li>
          <li>Customs, duties, and import taxes may be charged by the destination country.</li>
        </ul>
        <p>
          Contact <a href="mailto:cost@averlynxstore.one">cost@averlynxstore.one</a>
          before ordering if you need help confirming service to your location.
        </p>
      </>
    ),
  },
  {
    id: "order-processing",
    title: "Order Processing",
    content: (
      <>
        <p>
          Orders are generally processed on business days. Processing time is
          separate from carrier transit time and may increase during holidays,
          promotions, or periods of unusually high demand.
        </p>
        <p>
          You will receive a confirmation email with tracking information once
          your order ships, when tracking is supported by the selected service.
        </p>
        <p>
          If your order includes pre-order or made-to-order items, the estimated
          ship date will be noted on the product page and in your order
          confirmation.
        </p>
      </>
    ),
  },
  {
    id: "taxes-duties",
    title: "Taxes, Duties & Pricing",
    content: (
      <>
        <p>
          Product prices and any taxes collected by averlynxstore are displayed at
          checkout. Currency conversion or card-provider fees are controlled by
          your payment provider.
        </p>
        <p>
          International recipients are responsible for duties, import taxes,
          brokerage charges, or similar fees imposed by local authorities unless
          checkout expressly states that they are included.
        </p>
      </>
    ),
  },
  {
    id: "delayed-lost",
    title: "Delayed or Lost Packages",
    content: (
      <>
        <p>
          If your package has not arrived within the estimated delivery window,
          please:
        </p>
        <ol>
          <li>Check the carrier tracking for updates or delivery attempts</li>
          <li>Verify your shipping address is correct in your account</li>
          <li>
            Check with your local post office if a delivery notice was left
          </li>
          <li>
            Contact our customer service team if no resolution is found after
            the estimated delivery date plus 5 business days
          </li>
        </ol>
        <p>
          For a confirmed lost package, we will work with the carrier and review
          the available replacement or refund options under applicable law.
        </p>
      </>
    ),
  },
  {
    id: "address-changes",
    title: "Address Changes & Delivery Instructions",
    content: (
      <>
        <p>
          Review the delivery address before submitting an order. Contact us as
          soon as possible if a correction is needed, but a change cannot be
          guaranteed once processing begins.
        </p>
        <p>
          Carriers may reject a change, charge a redirection fee, or require the
          recipient to use a carrier-managed option. We will never ask for a fee
          through an unverified message or unrelated payment link.
        </p>
      </>
    ),
  },
  {
    id: "split-shipments",
    title: "Split Shipments & Partial Delivery",
    content: (
      <>
        <p>
          Items in one order may ship separately because of size, availability,
          or fulfillment location. Separate tracking may be provided for each
          parcel. We will not add an undisclosed shipping charge solely because
          we choose to split an accepted order.
        </p>
      </>
    ),
  },
  {
    id: "delivery-risk",
    title: "Delivery, Signatures & Risk of Loss",
    content: (
      <>
        <p>
          A carrier may require a signature, safe-place instruction, collection,
          or identity check. Follow tracking and arrange receipt when possible.
        </p>
        <p>
          For consumer orders, risk generally transfers when you or a person you
          designate receives the goods, except where applicable law provides a
          different rule or you independently selected and contracted with the
          carrier.
        </p>
      </>
    ),
  },
  {
    id: "damaged-packages",
    title: "Damaged, Opened or Incorrect Packages",
    content: (
      <>
        <p>
          If a parcel arrives damaged, opened, incomplete, or with the wrong
          item, photograph the outer packaging, label, contents, and damage when
          safe. Contact cost@averlynxstore.one promptly and keep the packaging
          until instructions are provided.
        </p>
        <p>
          We will assess the issue and provide the replacement, refund, or other
          remedy required by our policies and applicable law.
        </p>
      </>
    ),
  },
  {
    id: "unclaimed-refused",
    title: "Refused, Unclaimed & Undeliverable Parcels",
    content: (
      <>
        <p>
          A parcel may be returned if the address is incomplete, delivery is
          refused, required charges are not paid, or the parcel is not collected.
          After it returns, we will contact you about reshipment or refund.
        </p>
        <p>
          Reasonable carrier or reshipping costs may be deducted only when they
          were caused by customer-provided information or refusal and when the
          deduction is permitted by law. This does not apply to a lawful
          rejection of faulty, damaged, or misdescribed goods.
        </p>
      </>
    ),
  },
];

function AccordionItem({ item, isOpen, onToggle }) {
  return (
    <div className="accordion-item">
      <button
        id={`accordion-trigger-${item.id}`}
        type="button"
        className="accordion-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`accordion-body-${item.id}`}
      >
        <span className="accordion-trigger__title">{item.title}</span>
        <span className={`accordion-trigger__icon${isOpen ? " is-open" : ""}`}>
          ▲
        </span>
      </button>
      <div
        id={`accordion-body-${item.id}`}
        className={`accordion-body${isOpen ? " is-open" : ""}`}
        role="region"
        aria-labelledby={`accordion-trigger-${item.id}`}
      >
        <div className="accordion-body__inner">{item.content}</div>
      </div>
    </div>
  );
}

function Delivery() {
  const [openId, setOpenId] = useState("shipping-methods");

  const handleToggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="delivery">
      <Header />

      <main className="delivery__content">
        <h1 className="delivery__page-title">Delivery & Shipping</h1>
        <p>Last updated: October 16, 2026</p>
        <div className="delivery__accordion">
          {DELIVERY_ITEMS.map((item) => (
            <AccordionItem
              key={item.id}
              item={item}
              isOpen={openId === item.id}
              onToggle={() => handleToggle(item.id)}
            />
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Delivery;
